import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { money } from '../events/util';

// EL TRÁMITE DEL CERTIFICADO, EN TRES PASOS.
//
// Sin certificado no se puede facturar, y el trámite es la parte donde la gente se traba:
// hay que generar un pedido, subirlo a ARCA, bajar el certificado y después asociarlo al
// servicio. Por eso la pantalla muestra los pasos con su estado en vez de un formulario
// suelto, y los errores que devuelve ARCA se muestran tal cual: son los que dicen en qué
// paso quedó trabado.
export default function ArcaCertificado({ onCambio }) {
  const api = useApi();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [pegar, setPegar] = useState('');
  const [entorno, setEntorno] = useState('homologacion');
  const [prueba, setPrueba] = useState(null);
  const [emision, setEmision] = useState(null);

  const load = useCallback(() => {
    api.get('/api/arca/certificado')
      .then(d => { setSt(d); setEntorno(d.entorno || 'homologacion'); })
      .catch(e => setErr(e.message));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  async function generarCsr() {
    setBusy('csr'); setErr(''); setMsg('');
    try {
      const d = await api.post('/api/arca/certificado/csr', { alias: 'tukipro' });
      // Se descarga en vez de mostrarse: lo que sigue es subir ESTE archivo a ARCA.
      const blob = new Blob([d.csr], { type: 'application/x-pem-file' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'tukipro-pedido-certificado.csr';
      a.click();
      URL.revokeObjectURL(a.href);
      setMsg('Pedido generado y descargado. Subilo a ARCA y volvé con el certificado.');
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  async function subirArchivo(e) {
    const file = e.target.files?.[0]; if (!file) return;
    try { setPegar(await file.text()); setErr(''); }
    catch { setErr('No se pudo leer el archivo.'); }
  }

  async function guardarCert() {
    setBusy('cert'); setErr(''); setMsg('');
    try {
      const d = await api.put('/api/arca/certificado', { certificado: pegar, entorno });
      setMsg(`Certificado cargado (${d.cert_cn || 'sin nombre'}).`);
      setPegar('');
      load(); onCambio && onCambio();
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  async function probar() {
    setBusy('probar'); setErr(''); setMsg(''); setPrueba(null);
    try {
      const d = await api.post('/api/arca/probar', {});
      setPrueba(d);
    } catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  // Pide un CAE de verdad contra homologación. Es lo único que prueba el ARMADO del
  // comprobante: la conexión puede estar perfecta y el comprobante rechazarse igual por
  // un importe que no cierra al centavo o una alícuota que no corresponde.
  async function emitirPrueba() {
    setBusy('emitir'); setErr(''); setEmision(null);
    try { setEmision(await api.post('/api/arca/probar-emision', { total: 1000 })); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  async function desvincular() {
    if (!window.confirm('¿Desvincular el certificado?\n\nSe borran la clave y el certificado, y se apaga la facturación. Los comprobantes ya emitidos no se tocan: existen ante ARCA.')) return;
    setBusy('del'); setErr('');
    try { await api.del('/api/arca/certificado'); setPrueba(null); load(); onCambio && onCambio(); }
    catch (e) { setErr(e.message); } finally { setBusy(''); }
  }

  if (!st) return <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>{err || 'Cargando…'}</div>;

  const porVencer = st.dias_para_vencer != null && st.dias_para_vencer <= 30;

  return (
    <div className="lb-card" style={{ padding: '24px 26px', marginTop: 16 }}>
      <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Certificado digital</div>
      <p style={{ fontSize: 12.5, color: 'var(--ink-600)', margin: '4px 0 18px', lineHeight: 1.6 }}>
        Es lo que le prueba a ARCA que los comprobantes los emitís vos. El trámite se hace una vez
        y se renueva cuando vence. <b style={{ color: 'var(--ink-800)' }}>Tu clave privada se genera
        y se guarda acá, cifrada: no viaja a ningún lado.</b>
      </p>

      {/* AVISO GRANDE MIENTRAS SE PRUEBA. Con el certificado de homologación las ventas
          se siguen facturando solas, pero contra el entorno de prueba de ARCA: esos
          comprobantes no existen fiscalmente. Confundirlo con producción es creer que se
          está facturando cuando no. */}
      {st.tiene_certificado && st.entorno !== 'produccion' && (
        <div style={{ border: '1px solid rgba(212,175,55,0.5)', background: 'rgba(212,175,55,0.08)', borderRadius: 6, padding: '12px 16px', marginBottom: 14, fontSize: 12.5, color: '#d4af37', lineHeight: 1.6 }}>
          <b>Estás en pruebas.</b> Las ventas se facturan contra el entorno de testing de ARCA:
          esos comprobantes <b>no tienen validez fiscal</b> y al comprador no se le manda nada.
          Para facturar de verdad hace falta el certificado de producción.
        </div>
      )}

      {msg && <div style={{ border: '1px solid rgba(46,204,113,0.5)', background: 'rgba(46,204,113,0.08)', borderRadius: 6, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#2ecc71' }}>{msg}</div>}
      {err && <div className="lb-err" style={{ marginBottom: 14 }}>{err}</div>}

      {/* PASO 1 */}
      <Paso n="1" titulo="Generar el pedido de certificado" hecho={st.tiene_clave}>
        <p className="tkp-p">Se genera acá y se descarga un archivo <code>.csr</code>. Es público: no contiene tu clave.</p>
        <button className="lb-btn lb-btn--white lb-btn--sm" disabled={busy === 'csr'} onClick={generarCsr}>
          {busy === 'csr' ? 'Generando…' : st.tiene_clave ? 'Generar uno nuevo' : 'Generar y descargar'}
        </button>
        {st.tiene_clave && !st.tiene_certificado && (
          <div style={{ fontSize: 11.5, color: '#d4af37', marginTop: 8 }}>Pedido generado. Falta subirlo a ARCA y volver con el certificado.</div>
        )}
      </Paso>

      {/* PASO 2 */}
      <Paso n="2" titulo="Subirlo a ARCA y bajar el certificado" hecho={st.tiene_certificado}>
        <p className="tkp-p">
          Con tu clave fiscal: para <b>pruebas</b>, en la aplicación <b>WSASS</b>; para <b>producción</b>, en
          <b> Administrador de Certificados Digitales</b>. Subís el <code>.csr</code> y bajás un <code>.crt</code>.
        </p>
        <p className="tkp-p">
          Después hay que <b>asociar ese certificado al servicio «wsfe»</b> (en WSASS para pruebas, en
          <b> Administrador de Relaciones</b> para producción). Sin ese paso, ARCA rechaza la conexión
          aunque el certificado esté bien.
        </p>
      </Paso>

      {/* PASO 3 */}
      <Paso n="3" titulo="Cargar el certificado acá" hecho={st.tiene_certificado}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {[['homologacion', 'Pruebas (homologación)'], ['produccion', 'Producción']].map(([k, t]) => (
            <button key={k} className={'lb-btn lb-btn--sm ' + (entorno === k ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setEntorno(k)}>{t}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ cursor: 'pointer' }}>
            Elegir archivo .crt<input type="file" accept=".crt,.pem,.cer,text/plain" onChange={subirArchivo} style={{ display: 'none' }} />
          </label>
          <span style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>o pegalo abajo</span>
        </div>
        <textarea className="lb-input" rows={4} value={pegar} onChange={e => setPegar(e.target.value)}
          placeholder="-----BEGIN CERTIFICATE-----" style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }} />
        <button className="lb-btn lb-btn--primary lb-btn--sm" style={{ marginTop: 10 }} disabled={!pegar.trim() || busy === 'cert'} onClick={guardarCert}>
          {busy === 'cert' ? 'Guardando…' : 'Guardar certificado'}
        </button>
      </Paso>

      {st.tiene_certificado && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 16, paddingTop: 16 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.7 }}>
            <div>Certificado: <b style={{ color: '#fff' }}>{st.cert_cn || '—'}</b> · entorno <b style={{ color: '#fff' }}>{st.entorno === 'produccion' ? 'producción' : 'pruebas'}</b></div>
            {st.cert_hasta && (
              <div style={{ color: porVencer ? '#d4af37' : 'var(--ink-700)' }}>
                Vence el {new Date(st.cert_hasta).toLocaleDateString('es-AR')}
                {st.dias_para_vencer != null && ` · ${st.dias_para_vencer} días`}
                {porVencer && ' — renovalo antes de que corte la facturación'}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="lb-btn lb-btn--white lb-btn--sm" disabled={busy === 'probar'} onClick={probar}>
              {busy === 'probar' ? 'Probando…' : 'Probar conexión'}
            </button>
            {st.entorno !== 'produccion' && (
              <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={busy === 'emitir'} onClick={emitirPrueba}>
                {busy === 'emitir' ? 'Emitiendo…' : 'Emitir comprobante de prueba'}
              </button>
            )}
            <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ color: 'var(--red-400)' }} disabled={busy === 'del'} onClick={desvincular}>Desvincular</button>
          </div>
          {emision && (
            <div style={{ border: '1px solid rgba(46,204,113,0.5)', background: 'rgba(46,204,113,0.08)', borderRadius: 6, padding: '12px 16px', marginTop: 12, fontSize: 12.5, color: '#2ecc71', lineHeight: 1.7 }}>
              <b>Comprobante emitido en pruebas.</b><br />
              Factura {String(emision.puntoVenta).padStart(4, '0')}-{String(emision.numero).padStart(8, '0')} por {money(emision.total)}
              {' '}(neto {money(emision.neto)} + IVA {money(emision.iva)})<br />
              CAE {emision.cae}{emision.caeVto ? ` · vence ${emision.caeVto}` : ''}
              <div style={{ color: 'var(--ink-600)', marginTop: 6 }}>Es del entorno de prueba de ARCA: no existe fiscalmente.</div>
            </div>
          )}
          {prueba && (
            <div style={{ border: '1px solid rgba(46,204,113,0.5)', background: 'rgba(46,204,113,0.08)', borderRadius: 6, padding: '12px 16px', marginTop: 12, fontSize: 12.5, color: '#2ecc71', lineHeight: 1.6 }}>
              Conexión OK con ARCA ({prueba.entorno === 'produccion' ? 'producción' : 'pruebas'}).<br />
              Punto de venta {prueba.puntoVenta} · último comprobante autorizado: {prueba.ultimoNumero} · el próximo sería el {prueba.proximo}.
            </div>
          )}
        </div>
      )}

      <style>{`.tkp-p{font-size:12.5px;color:var(--ink-700);line-height:1.6;margin:0 0 10px}
        .tkp-p code{background:var(--ink-050);padding:1px 5px;border-radius:3px;font-size:11.5px}`}</style>
    </div>
  );
}

function Paso({ n, titulo, hecho, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{
        width: 26, height: 26, flex: 'none', borderRadius: '50%', display: 'grid', placeItems: 'center',
        fontSize: 12, fontWeight: 700,
        background: hecho ? 'rgba(46,204,113,0.15)' : 'var(--ink-050)',
        color: hecho ? '#2ecc71' : 'var(--ink-600)',
        border: '1px solid ' + (hecho ? 'rgba(46,204,113,0.5)' : 'var(--border)'),
      }}>{hecho ? '✓' : n}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#fff', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{titulo}</div>
        {children}
      </div>
    </div>
  );
}
